import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import './residents-page.css';
import NavigationV2 from '../../../components/v2/NavigationV2.jsx';
import ResidentTable, { COLUMN_DEFS } from './components/ResidentTable.jsx';
import usePersistedColumns from '../../../hooks/usePersistedColumns.js';
import usePersistedPerPage from '../../../hooks/usePersistedPerPage.js';
import ResidentDrawer from './components/ResidentDrawer.jsx';
import RelocateDialog from './components/RelocateDialog.jsx';
import MoveOutDialog from './components/MoveOutDialog.jsx';
import ConfirmTypedDialog from '../../../components/v2/ConfirmTypedDialog.jsx';
import BulkGroupDrawer from './components/BulkGroupDrawer.jsx';
import EasyAddDeviceDrawer from '../../../components/v2/EasyAddDeviceDrawer.jsx';
import { addDevice } from '../../devices/api/devicesApi.js';
import DeviceDrawer from './components/DeviceDrawer.jsx';
import GroupsTable, { GROUP_COLUMN_DEFS } from './components/GroupsTable.jsx';
import GroupDrawer from './components/GroupDrawer.jsx';
import ActiveFilterBar from './components/ActiveFilterBar.jsx';
import BulkActionBar from './components/BulkActionBar.jsx';
import ColumnVisibilityToggle from './components/ColumnVisibilityToggle.jsx';
import TableSkeleton from './components/TableSkeleton.jsx';
// V2 data layer — real API calls
import { getSession, getCampusId, getUserId, getSessionId, getOffsiteMonitoringVendors, getActiveVendorIdList, getAllVendors, getCampusList, updateSessionCampus, getRoleWeight, isResidentDataEhrManaged, isResidentEditLocked, getEhrInfo } from '../../../services/session.js';
import { useNotifications } from '../../../contexts/NotificationContext.jsx';
import { alertsByResident, activeAlertResidentIds, activeAlertDeviceIds } from '../../../utils/activeAlerts.js';
import { iAmHigherAuthority, convertHttpUtcToLocal } from '../../../libs.jsx';
import { fetchResidents, fetchInovonicsLocations, fetchAreas, fetchDevices, fetchWanderingDevices, fetchGroupList, addResident, editResident, moveOutResidents, deleteResidents, assignDevice, unassignDevice, relocateResident, bulkEditResidentGroups } from './api/residentsApi.js';
import { fetchGroups, fetchGroupCategories, addGroup, editGroup, deleteGroups } from './api/groupsApi.js';
import { exportResidentsCsv, exportGroupsCsv } from '../../../services/csvExport.js';

/** All column keys derived from COLUMN_DEFS */
const ALL_COLUMN_KEYS = COLUMN_DEFS.map((c) => c.key);

/** Default visible columns — show all by default (user can hide via column toggle) */
const DEFAULT_VISIBLE = ALL_COLUMN_KEYS;

/** Newly-added columns surfaced once for existing users with a saved preference. */
const FORCE_INCLUDE_COLUMNS = ['locating', 'ehr_resident_id'];

/**
 * Merge the latest Inovonics RTLS location fetch onto residents by device serial.
 *
 * Each location entry is shaped { txid, location: { area_name, area_id, ... } },
 * where txid is the transmitter serial — equal to an attached device's
 * manufacture_uuid.
 *
 * Last-known-location semantics: a resident's `location` is only updated when the
 * incoming fetch provides a usable area name. If the fetch returns nothing/blank
 * for a resident, their existing location is kept rather than cleared — so a
 * transient blank reading on a poll never wipes out the last known location.
 *
 * The payload's UTC `timestamp` is converted to campus-local time (tzOffset)
 * and stored alongside the area for the column's hover popover.
 *
 * @param {Array} residents
 * @param {Array} locationData — response from fetchInovonicsLocations
 * @param {number} tzOffset — campus tz_offset (signed hours from UTC)
 * @returns {Array}
 */
function applyLocations(residents, locationData, tzOffset = 0) {
  const byTxid = new Map();
  (locationData || []).forEach((entry) => {
    if (entry?.txid != null && entry?.location) {
      byTxid.set(String(entry.txid), entry.location);
    }
  });
  return (residents || []).map((r) => {
    const match = (r.devices || []).find(
      (d) => d?.manufacture_uuid != null && byTxid.has(String(d.manufacture_uuid)),
    );
    const newLoc = match ? byTxid.get(String(match.manufacture_uuid)) : null;
    if (newLoc?.area_name) {
      return {
        ...r,
        location: {
          area_name: newLoc.area_name,
          area_id: newLoc.area_id,
          timestamp: convertHttpUtcToLocal(newLoc.timestamp, tzOffset),
        },
      };
    }
    return r; // no fresh/usable location — keep last known
  });
}

/** All group column keys */
const ALL_GROUP_KEYS = GROUP_COLUMN_DEFS.map((c) => c.key);

/** Default visible columns for groups table — show all by default */
const DEFAULT_GROUP_VISIBLE = ALL_GROUP_KEYS;

/**
 * ResidentsPage — main orchestrator for the /community/residents V2 redesign.
 *
 * Manages: real API data loading, search, column filters, sort, pagination,
 * row selection, drawer state, and tab switching.
 *
 * Data layer: Uses session-based auth (encrypted dData from localStorage)
 * and calls the same backend endpoints as V1, via the V2 api client
 * (src/services/api.js) with domain-specific modules in ./api/.
 */
const ResidentsPage = ({ initialTab = 'residents' }) => {
  // ---------- Session ----------
  const sessionCampusId = getCampusId();
  const campusList = useMemo(() => getCampusList() || [], []);

  // ---------- Data & Loading ----------
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [campusId, setCampusId] = useState(sessionCampusId || 0);

  // ---------- Lookup Data (from API) ----------
  const [areas, setAreas] = useState([]);
  const [groupList, setGroupList] = useState([]);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [groupCategories, setGroupCategories] = useState([]);
  const firstLoad = useRef(true);

  // ---------- Tab ----------
  const activeTab = initialTab;

  // ---------- Search & Filters ----------
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [filters, setFilters] = useState({});

  // ---------- Sort ----------
  const [sortColumn, setSortColumn] = useState('resident_last_name');
  const [sortOrder, setSortOrder] = useState('asc');

  // ---------- Pagination ----------
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = usePersistedPerPage();

  // ---------- Locating (RTLS) gate — active vendor 14 on the current campus ----------
  const locatingActive = useMemo(
    () => getAllVendors().some((v) => v.vendor_active && v.vendor_id === 14),
    [campusId],
  );

  // Campus UTC offset for converting RTLS location timestamps to local time.
  const tzOffset = useMemo(
    () => parseInt(getSession()?.campusData?.[campusId?.toString()]?.tz_offset ?? 0, 10) || 0,
    [campusId],
  );

  /* ---------- EHR gate ----------
     The EHR ID column only exists where a partner system is actually integrated.
     Keyed on vendor_active alone, not on isResidentDataEhrManaged: an id is worth
     seeing wherever the integration exists, even on a campus that only sends and
     does not receive residents. */
  const ehrActive = useMemo(() => getEhrInfo(campusId).active, [campusId]);

  // ---------- Column Visibility ----------
  const [visibleColumns, handleColumnToggle] = usePersistedColumns('residents', DEFAULT_VISIBLE, ALL_COLUMN_KEYS, FORCE_INCLUDE_COLUMNS);

  /* Columns that only exist under a campus condition, keyed so a third one does not
     mean another pair of near-identical filter expressions. A key absent from this
     map is ungated, which is why the test is `=== false` rather than a truthiness
     check — `undefined` must pass. */
  const gatedColumns = useMemo(
    () => ({ locating: locatingActive, ehr_resident_id: ehrActive }),
    [locatingActive, ehrActive],
  );

  /* Filtered out of BOTH the column toggle and the table's visible set: leaving a
     gated key in the visible set renders a header with no data behind it, and
     leaving it in the toggle offers a column that cannot be shown. */
  const availableColumns = useMemo(
    () => COLUMN_DEFS.filter((c) => gatedColumns[c.key] !== false),
    [gatedColumns],
  );
  const effectiveVisibleColumns = useMemo(
    () => visibleColumns.filter((k) => gatedColumns[k] !== false),
    [gatedColumns, visibleColumns],
  );

  // ---------- Row Selection ----------
  const [selectedIds, setSelectedIds] = useState([]);

  // ---------- Drawers ----------
  const [residentDrawerOpen, setResidentDrawerOpen] = useState(false);
  const [editingResident, setEditingResident] = useState(null);
  const [deviceDrawerOpen, setDeviceDrawerOpen] = useState(false);
  const [deviceResident, setDeviceResident] = useState(null);
  // Relocate dialog — holds the resident row being moved (null = closed)
  const [relocateResidentRow, setRelocateResidentRow] = useState(null);
  // Move-out confirmation (styled dialog, shared with the profile page)
  const [moveOutRow, setMoveOutRow] = useState(null);
  const [moveOutSaving, setMoveOutSaving] = useState(false);
  const [bulkMoveOutOpen, setBulkMoveOutOpen] = useState(false);
  const [bulkMoveOutSaving, setBulkMoveOutSaving] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteSaving, setBulkDeleteSaving] = useState(false);
  const [relocateSaving, setRelocateSaving] = useState(false);
  // Easy add-device drawer (opened from the manage-devices drawer)
  const [easyAddOpen, setEasyAddOpen] = useState(false);
  // Bulk group edit drawer
  const [bulkGroupDrawerOpen, setBulkGroupDrawerOpen] = useState(false);
  const [bulkGroupSaving, setBulkGroupSaving] = useState(false);

  // ---------- Page-level Toast ----------
  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message: string }
  const toastTimerRef = useRef(null);

  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // ==========================================================================
  // GROUPS TAB STATE
  // ==========================================================================
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupSearch, setGroupSearch] = useState('');
  const [groupFilters, setGroupFilters] = useState({});
  const [groupSortColumn, setGroupSortColumn] = useState('resident_group_name');
  const [groupSortOrder, setGroupSortOrder] = useState('asc');
  const [groupPage, setGroupPage] = useState(1);
  const [groupPerPage, setGroupPerPage] = usePersistedPerPage();
  const [groupVisibleColumns, setGroupVisibleColumns] = useState(DEFAULT_GROUP_VISIBLE);
  const [groupSelectedIds, setGroupSelectedIds] = useState([]);
  const [groupDrawerOpen, setGroupDrawerOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  /* Deleting a group is destructive and was previously unguarded from the table
     kebab, so both the single and bulk paths now route through a typed
     confirmation. `deleteGroupRow` doubles as the open flag. */
  const [deleteGroupRow, setDeleteGroupRow] = useState(null);
  const [deleteGroupSaving, setDeleteGroupSaving] = useState(false);
  const [groupBulkDeleteOpen, setGroupBulkDeleteOpen] = useState(false);
  const [groupBulkDeleteSaving, setGroupBulkDeleteSaving] = useState(false);

  // ---------- Helper: get session params for API write calls ----------
  const getSessionParams = useCallback(() => {
    return {
      campusId,
      userId: getUserId(),
      clientId: getSessionId(),
      vendor_ids: getActiveVendorIdList(),
    };
  }, [campusId]);

  // ---------- Load residents + lookup data from API ----------
  const loadResidents = useCallback(async () => {
    setLoading(true);
    try {
      const isFirstLoad = firstLoad.current;
      const [resData, areaData, inovonicsDevices, wanderingDevices, grpList, locationData] = await Promise.all([
        fetchResidents(campusId, !isFirstLoad),
        fetchAreas(campusId),
        fetchDevices(campusId),
        fetchWanderingDevices(campusId, !isFirstLoad),
        fetchGroupList(campusId),
        // Inovonics RTLS locations — only when an active vendor 14 is configured.
        // readOnly stays true: it only gates MySQL reads, and this data comes
        // live from the third-party (Inovonics) API.
        locatingActive
          ? fetchInovonicsLocations(campusId, true).catch((locErr) => {
              console.error('[ResidentsPage] fetchInovonicsLocations error:', locErr);
              return [];
            })
          : Promise.resolve(null),
      ]);
      firstLoad.current = false;
      // When RTLS is active, attach the most-recent location to each resident by
      // matching location.txid to an attached device's manufacture_uuid. Seed the
      // freshly-fetched rows with the last-known location from current state so a
      // full reload doesn't blank a known location, then apply the latest fetch.
      if (locatingActive) {
        setResidents((prev) => {
          const priorLoc = new Map();
          (prev || []).forEach((p) => { if (p.location) priorLoc.set(p.resident_id, p.location); });
          const seeded = (resData || []).map((r) =>
            priorLoc.has(r.resident_id) ? { ...r, location: priorLoc.get(r.resident_id) } : r,
          );
          return applyLocations(seeded, locationData, tzOffset);
        });
      } else {
        setResidents(resData || []);
      }
      setAreas(areaData || []);
      // Wandering devices use `model_name`; Inovonics use `inovonics_model_name`.
      // Normalize so DeviceDrawer's meta line shows a model for both.
      const normalizedWandering = (wanderingDevices || []).map((d) => ({
        ...d,
        inovonics_model_name: d.inovonics_model_name || d.model_name || '',
      }));
      setAvailableDevices([...(inovonicsDevices || []), ...normalizedWandering]);
      setGroupList(grpList || []);
    } catch (err) {
      console.error('[ResidentsPage] Failed to load residents data:', err);
    } finally {
      setLoading(false);
    }
  }, [campusId, locatingActive, tzOffset]);

  useEffect(() => { loadResidents(); }, [loadResidents]);

  // ---------- Poll RTLS locations every 60s (last-known preserved) ----------
  // Only refetches the lightweight location feed and merges it into the current
  // residents; applyLocations keeps a resident's last known location when a poll
  // returns blank. readOnly stays true — it only gates MySQL reads, and this
  // feed comes live from the third-party (Inovonics) API.
  useEffect(() => {
    if (!locatingActive) return undefined;
    const interval = setInterval(async () => {
      try {
        const locationData = await fetchInovonicsLocations(campusId, true);
        setResidents((prev) => applyLocations(prev, locationData, tzOffset));
      } catch (err) {
        console.error('[ResidentsPage] RTLS location poll error:', err);
      }
    }, 60000);
    return () => clearInterval(interval);
  }, [locatingActive, campusId, tzOffset]);

  // ---------- Load groups + categories from API ----------
  const loadGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      const [grpData, catData] = await Promise.all([
        fetchGroups(campusId),
        fetchGroupCategories(),
      ]);
      setGroups(grpData || []);
      setGroupCategories(catData || []);
    } catch (err) {
      console.error('[ResidentsPage] Failed to load groups data:', err);
    } finally {
      setGroupsLoading(false);
    }
  }, [campusId]);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  // ---------- Filter / Search / Sort pipeline ----------
  const filteredData = useMemo(() => {
    let data = [...residents];

    // 1. Active/inactive filter
    if (!showInactive) {
      data = data.filter((r) => r.is_active);
    }

    // 2. Search
    if (search.trim()) {
      const s = search.toLowerCase();
      data = data.filter(
        (r) =>
          r.resident_first_name?.toLowerCase().includes(s) ||
          r.resident_last_name?.toLowerCase().includes(s) ||
          r.resident_nickname?.toLowerCase().includes(s) ||
          r.area_name?.toLowerCase().includes(s) ||
          r.resident_phone?.includes(s) ||
          r.resident_email?.toLowerCase().includes(s) ||
          `${r.resident_first_name} ${r.resident_last_name}`.toLowerCase().includes(s) ||
          (r.devices || []).some((d) => d.device_name?.toLowerCase().includes(s))
      );
    }

    // 3. Column filters
    Object.entries(filters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;

      data = data.filter((row) => {
        if (columnKey === 'resident_groups') {
          const groups = row.resident_groups || [];
          if (values.includes('(empty)') && groups.length === 0) return true;
          return groups.some((g) => values.includes(g.resident_group_name));
        }
        if (columnKey === 'devices') {
          const devs = row.devices || [];
          if (values.includes('(empty)') && devs.length === 0) return true;
          return devs.some((d) => values.includes(d.device_name));
        }
        if (columnKey === 'status') {
          const status = row.status || (row.is_active ? 'Active' : 'Moved Out');
          return values.includes(status);
        }
        const val = row[columnKey];
        const strVal = val != null && val !== '' ? String(val) : '(empty)';
        return values.includes(strVal);
      });
    });

    // 4. Sort
    data.sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      if (sortColumn === 'status') {
        aVal = a.status || (a.is_active ? 'Active' : 'Moved Out');
        bVal = b.status || (b.is_active ? 'Active' : 'Moved Out');
      } else if (sortColumn === 'locating') {
        // Locating value is a nested object; sort by the displayed area name.
        aVal = a.location?.area_name;
        bVal = b.location?.area_name;
      }

      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      const result = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return sortOrder === 'asc' ? result : -result;
    });

    return data;
  }, [residents, showInactive, search, filters, sortColumn, sortOrder]);

  // ---------- Pagination ----------
  const totalFiltered = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
  const safePage = Math.min(page, totalPages);

  const paginatedData = useMemo(() => {
    const start = (safePage - 1) * perPage;
    return filteredData.slice(start, start + perPage);
  }, [filteredData, safePage, perPage]);

  const showingStart = totalFiltered === 0 ? 0 : (safePage - 1) * perPage + 1;
  const showingEnd = Math.min(safePage * perPage, totalFiltered);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, filters, showInactive, perPage]);

  // ---------- Handlers ----------
  const handleSort = useCallback(
    (column) => {
      if (sortColumn === column) {
        setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortColumn(column);
        setSortOrder('asc');
      }
    },
    [sortColumn]
  );

  const handleFilterChange = useCallback((columnKey, values) => {
    setFilters((prev) => ({ ...prev, [columnKey]: values }));
  }, []);

  const handleRemoveFilter = useCallback((columnKey, value) => {
    setFilters((prev) => ({
      ...prev,
      [columnKey]: (prev[columnKey] || []).filter((v) => v !== value),
    }));
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilters({});
    setSearch('');
  }, []);

  const handleSelectRow = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    const allIds = paginatedData.map((r) => r.resident_id);
    const allSelected = allIds.every((id) => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  }, [paginatedData, selectedIds]);

  // Drawer handlers
  const openAddResident = useCallback(() => {
    setEditingResident(null);
    setResidentDrawerOpen(true);
  }, []);

  const openDeviceDrawer = useCallback((resident) => {
    setDeviceResident(resident);
    setDeviceDrawerOpen(true);
  }, []);

  const handleSaveResident = useCallback(
    async (formData) => {
      const session = getSessionParams();
      let result;

      // Map resident_groups objects to plain ID array for the API
      const groupIds = (formData.resident_groups || []).map((g) => g.resident_group_id);

      // Build API-ready data with correct field mappings
      const apiData = {
        ...formData,
        resident_group_ids: groupIds,
        ehr_resident_id: formData.ehr_resident_id || '',
      };

      if (formData.resident_id) {
        // Edit mode — find the original data for the OldPayload
        const original = residents.find((r) => r.resident_id === formData.resident_id);
        const oldGroupIds = (original?.resident_groups || []).map((g) => g.resident_group_id);
        const oldApiData = {
          ...original,
          resident_group_ids: oldGroupIds,
        };
        result = await editResident(apiData, oldApiData, session);
      } else {
        // Add mode
        result = await addResident(apiData, session);
      }

      if (result[0]) {
        setResidentDrawerOpen(false);
        loadResidents();
        showToast('success', formData.resident_id
          ? `${formData.resident_first_name} ${formData.resident_last_name} updated`
          : `${formData.resident_first_name} ${formData.resident_last_name} added`
        );
      } else {
        showToast('error', result[1] || 'Failed to save resident');
      }
    },
    [residents, getSessionParams, loadResidents, showToast]
  );

  /* ---------- Active alarms (Phase III of docs/ACTIVE_ALARM_GATING.md) ----------
     One shared subscription, no fetch and no is_cloud check — see that doc.

     DECLARED HERE, HIGH IN THE COMPONENT, ON PURPOSE. Everything that gates on an
     alarm lists these in a dependency array — the device handlers immediately
     below, getAlarmBlockedReason, isRowLocked's sibling bulk plans — and a dep
     array is built when its useCallback line runs, so a value declared lower throws
     "Cannot access 'X' before initialization" on first render. vite build does not
     catch it. Keep this block above its consumers. */
  const { activeCloudAlarms, activeAlarms } = useNotifications();

  const alertedResidentIds = useMemo(
    () => activeAlertResidentIds(activeCloudAlarms, activeAlarms),
    [activeCloudAlarms, activeAlarms],
  );

  /* The same grouping the row marker renders from. activeAlertResidentIds is derived
     from this map inside the helper, so the action gates and the row marker can
     never disagree about which residents are alarming. */
  const residentAlertMap = useMemo(
    () => alertsByResident(activeCloudAlarms, activeAlarms),
    [activeCloudAlarms, activeAlarms],
  );

  const alertedDeviceIds = useMemo(
    () => activeAlertDeviceIds(activeCloudAlarms, activeAlarms),
    [activeCloudAlarms, activeAlarms],
  );

  /* Why these three actions and not Edit: move out, delete and relocate all change
     or end the assignment the alarm is running against, which is what can swallow
     the device's clear signal. Renaming a resident does not, so Edit stays open —
     deliberately narrower than the EHR lock, which does cover Edit. */
  const getAlarmBlockedReason = useCallback(
    (row) => (alertedResidentIds.has(String(row?.resident_id))
      ? 'This resident has an active alarm. Wait until the alert is resolved.'
      : null),
    [alertedResidentIds],
  );

  const handleAssignDevice = useCallback(async (residentId, deviceId) => {
    const device = availableDevices.find((d) => d.device_id === deviceId);
    if (!device) return [false, 'Device not found'];
    /* Defense-in-depth: the drawer already disables Assign/Reassign for an alarming
       device, but an alert can start between render and click, and
       handleEasyAssignExisting reaches this without passing through that UI. */
    if (alertedDeviceIds.has(String(deviceId))) {
      return [false, 'This device has an active alarm. Wait until the alert is resolved before reassigning it.'];
    }

    const resident = residents.find((r) => r.resident_id === residentId);
    const residentAreaId = resident?.area_id || 0;

    const session = getSessionParams();
    const result = await assignDevice(device, residentId, session, residentAreaId);

    if (result[0]) {
      await loadResidents(); // Refresh data from backend
    }
    return result;
  }, [availableDevices, residents, getSessionParams, loadResidents, alertedDeviceIds]);

  // Easy add-device flow: creates the device already assigned to the resident
  // (device_add_resident accepts resident_id, so no separate assign call).
  const handleEasyAddDevice = useCallback(async (formData) => {
    const session = getSessionParams();
    const result = await addDevice(formData, 'device_add_resident', session);
    if (result[0]) {
      await loadResidents();
      showToast('success', `${formData.device_name || 'Device'} added and assigned`);
    }
    return result;
  }, [getSessionParams, loadResidents, showToast]);

  // Duplicate serial in the easy flow — assign the existing device instead of
  // erroring, so the user never needs to know whether it was already added.
  const handleEasyAssignExisting = useCallback(async (device) => {
    const target = deviceResident;
    if (!target) return [false, 'No resident selected'];
    const session = getSessionParams();
    const result = await assignDevice(device, target.resident_id, session, target.area_id || 0);
    if (result[0]) {
      await loadResidents();
      showToast('success', `${device.device_name || 'Device'} assigned`);
    }
    return result;
  }, [deviceResident, getSessionParams, loadResidents, showToast]);

  const handleRemoveDevice = useCallback(async (residentId, deviceId, deviceName = null, manufactureId = 1) => {
    if (alertedDeviceIds.has(String(deviceId))) {
      return [false, `${deviceName || 'This device'} has an active alarm. Wait until the alert is resolved before unassigning it.`];
    }
    const session = getSessionParams();
    const result = await unassignDevice(deviceId, residentId, session, deviceName, manufactureId);

    if (result[0]) {
      await loadResidents(); // Refresh data from backend
    }
    return result;
  }, [getSessionParams, loadResidents, alertedDeviceIds]);

  // Current user's role weight — gates the admin-only Resident ID export column.
  const myRoleWeight = useMemo(() => getRoleWeight(), []);

  // Bulk action stubs
  const handleBulkExport = useCallback(() => {
    const selected = residents.filter((r) => selectedIds.includes(r.resident_id));
    if (selected.length) exportResidentsCsv(selected, 'residents-selected.csv', myRoleWeight, effectiveVisibleColumns);
  }, [selectedIds, residents, myRoleWeight, effectiveVisibleColumns]);

  // Page-level export: exports all currently-filtered residents
  const handleExportAll = useCallback(() => {
    if (filteredData.length) exportResidentsCsv(filteredData, 'residents.csv', myRoleWeight, effectiveVisibleColumns);
  }, [filteredData, myRoleWeight, effectiveVisibleColumns]);

  /* Bulk move out — same typed confirmation as the single-row dialog. A batch is
     the higher-stakes version of the same action, so it should not be the easier
     one to trigger. */
  /* Declared BEFORE bulkMoveOutTargetIds below, which lists isRowLocked in its
     dependency array. A dep array is constructed when its useMemo line runs, so a
     const declared further down throws a ReferenceError on first render — not a
     stylistic preference, a hard ordering requirement. */
  const residentDataEhrManaged = useMemo(
    () => isResidentDataEhrManaged(campusId),
    [campusId],
  );

  /* Role weights, named once so the two gates below read as the rule rather than as
     magic numbers: 10 is the ordinary edit right, 80 is the override that ignores EHR
     ownership entirely. */
  const hasBaseEditRole = useMemo(() => iAmHigherAuthority(10, myRoleWeight), [myRoleWeight]);
  const canOverrideEhrLock = useMemo(() => iAmHigherAuthority(80, myRoleWeight), [myRoleWeight]);

  /* The campus-wide lock switch. Only carries meaning where the EHR is actually
     receiving residents: with nothing being synced there is nothing for the partner
     to own, and the integration drawer will not let the flag be set in that state
     anyway (the toggle is disabled unless Receive Residents is on). */
  const residentLocked = useMemo(
    () => residentDataEhrManaged && getEhrInfo(campusId).resident_locked,
    [residentDataEhrManaged, campusId],
  );

  /* Adding a resident has no resident to test, so it turns on the campus-wide switch
     alone: weight > 80 always; otherwise base edit rights and resident_locked off.

     This previously keyed on `residentDataEhrManaged` — whether the campus receives
     residents at all — which hid the button on every integrated campus regardless of
     the lock switch. A weight-30 admin on a receiving campus with resident_locked
     FALSE is exactly the case the switch exists to allow, and it was being refused. */
  const canAddResidents = useMemo(
    () => canOverrideEhrLock || (hasBaseEditRole && !residentLocked),
    [canOverrideEhrLock, hasBaseEditRole, residentLocked],
  );

  /* Per-resident, which is the point of this rule: on a receiving campus a resident the
     partner owns (has an ehr_resident_id) is locked while a portal-created one is not,
     unless resident_locked is set campus-wide. Also covers "no edit role at all", so a
     single predicate decides whether a row offers any write action. */
  const isRowLocked = useCallback(
    (row) => {
      if (canOverrideEhrLock) return false;
      if (!hasBaseEditRole) return true;
      return isResidentEditLocked(row, campusId);
    },
    [canOverrideEhrLock, hasBaseEditRole, campusId],
  );

  /* WHY the row is locked, for the disabled Edit item's hover text. isRowLocked
     collapses three different situations into one boolean, and "you can't" without
     "because" is what generates support calls — so this re-derives which of the
     three applies and names it.

     Order matters: the campus-wide switch is checked before the per-resident id,
     because when it is set every resident is locked and blaming the individual
     record would send someone hunting for an id that is not the cause. */
  const getEditLockedReason = useCallback(
    (row) => {
      if (!isRowLocked(row)) return null;
      if (!hasBaseEditRole) return 'You do not have permission to edit residents.';
      const vendor = getEhrInfo(campusId).vendor_name;
      const partner = vendor ? `the ${vendor} integration` : 'the EHR integration';
      if (residentLocked) {
        return `Resident details for this campus are managed by ${partner}. Edit them there instead.`;
      }
      return `This resident is managed by ${partner}. Edit them there instead, or ask an administrator.`;
    },
    [isRowLocked, hasBaseEditRole, residentLocked, campusId],
  );

  /* Moved down from its original position above: it now depends on
     getEditLockedReason, and a dep array is built when its useCallback line runs, so
     it has to sit below what it references. */
  const openEditResident = useCallback((resident) => {
    /* The kebab item stays clickable so its title can surface (a disabled element
       fires no pointer events), so the refusal lives here. */
    const blocked = getEditLockedReason(resident);
    if (blocked) {
      showToast('error', blocked);
      return;
    }
    setEditingResident(resident);
    setResidentDrawerOpen(true);
  }, [getEditLockedReason, showToast]);

  /* Selection split into what a bulk write may touch and what it may not. Derived from
     the loaded rows, so a resident whose lock state changed since load is re-evaluated
     on the next fetch rather than trusted from the click. */
  const bulkMoveOutTargetIds = useMemo(
    () => selectedIds.filter((id) => {
      const row = residents.find((r) => String(r.resident_id) === String(id));
      if (!row) return false;
      return !isRowLocked(row) && !getAlarmBlockedReason(row);
    }),
    [selectedIds, residents, isRowLocked, getAlarmBlockedReason],
  );
  /* Counted separately rather than rolled into one "skipped" number, because the two
     reasons need different advice: an EHR-locked resident must be moved out in the
     partner system, an alarming one just needs a minute. */
  const bulkLockedCount = useMemo(
    () => selectedIds.filter((id) => {
      const row = residents.find((r) => String(r.resident_id) === String(id));
      return row ? isRowLocked(row) : false;
    }).length,
    [selectedIds, residents, isRowLocked],
  );
  const bulkAlarmCount = useMemo(
    () => selectedIds.filter((id) => {
      const row = residents.find((r) => String(r.resident_id) === String(id));
      /* Locked rows are already reported as locked; don't count them twice. */
      return row ? (!isRowLocked(row) && Boolean(getAlarmBlockedReason(row))) : false;
    }).length,
    [selectedIds, residents, isRowLocked, getAlarmBlockedReason],
  );

  /* Delete has its own target list. It deliberately does NOT inherit the EHR skip:
     delete is a weight-80 action that has always been able to remove an EHR-owned
     resident, and quietly narrowing that here would be a policy change nobody asked
     for. The alarm skip does apply. */
  const bulkDeleteTargetIds = useMemo(
    () => selectedIds.filter((id) => {
      const row = residents.find((r) => String(r.resident_id) === String(id));
      return row ? !getAlarmBlockedReason(row) : true;
    }),
    [selectedIds, residents, getAlarmBlockedReason],
  );
  const bulkDeleteAlarmCount = selectedIds.length - bulkDeleteTargetIds.length;

  const handleBulkMoveOut = useCallback(() => {
    if (selectedIds.length === 0) return;
    setBulkMoveOutOpen(true);
  }, [selectedIds.length]);

  const handleConfirmBulkMoveOut = useCallback(async () => {
    /* Locked residents are dropped rather than blocking the whole batch — selecting
       twenty and being refused because two are EHR-owned is worse than moving out the
       eighteen. The dialog states the skip count, so this is never silent. */
    const targetIds = bulkMoveOutTargetIds;
    const count = targetIds.length;
    if (count === 0) return;
    setBulkMoveOutSaving(true);
    const session = getSessionParams();
    const result = await moveOutResidents(targetIds, session);
    setBulkMoveOutSaving(false);
    if (result[0]) {
      setBulkMoveOutOpen(false);
      setSelectedIds([]);
      loadResidents();
      showToast('success', `${count} resident${count > 1 ? 's' : ''} moved out`);
    } else {
      /* Leave the dialog open on failure so the user keeps their place. */
      showToast('error', result[1] || 'Move out failed');
    }
  }, [bulkMoveOutTargetIds, getSessionParams, loadResidents, showToast]);

  /* ---------- Resident edit rights ----------
   *
   * On an EHR-integrated campus the partner system owns resident records and room
   * assignments, so hand-editing them here either gets overwritten on the next
   * sync or diverges from the customer's system of record. This page previously
   * applied no EHR gating at all, which left it as an unguarded route to the same
   * writes ResidentProfilePage already blocked — customers with an integration
   * could still add, edit, relocate and move out from the list.
   *
   * Same formula as ResidentProfilePage's `baseEditRights` (:139-141) on purpose:
   * weight > 80 keeps access so PalCare staff can still correct data, and any
   * future change to the rule has one shape to find in both places.
   *
   * Deliberately NOT gated: resident groups (a PalCare-only concept the EHR
   * doesn't own — the explicit carve-out), device assignment, and export.
   */
  // ---------- Permanent delete (role weight > 80) ----------
  const canDeleteResidents = useMemo(() => iAmHigherAuthority(80, myRoleWeight), [myRoleWeight]);

  const handleBulkDelete = useCallback(() => {
    if (!canDeleteResidents || selectedIds.length === 0) return;
    setBulkDeleteOpen(true);
  }, [canDeleteResidents, selectedIds.length]);

  const handleConfirmBulkDelete = useCallback(async () => {
    /* Re-check the right here, not just at open time: this is the irreversible
       one, and the dialog can outlive a session change. */
    if (!canDeleteResidents) return;
    /* Alarming residents are dropped, not blocking the batch — same reasoning as
       move out, and the dialog states the count. */
    const targetIds = bulkDeleteTargetIds;
    const count = targetIds.length;
    if (count === 0) {
      setBulkDeleteOpen(false);
      showToast('error', 'No selected residents can be deleted — all have an active alarm.');
      return;
    }
    setBulkDeleteSaving(true);
    const session = getSessionParams();
    const result = await deleteResidents(targetIds, session);
    setBulkDeleteSaving(false);
    if (result[0]) {
      setBulkDeleteOpen(false);
      setSelectedIds([]);
      loadResidents();
      showToast('success', `${count} resident${count > 1 ? 's' : ''} deleted`);
    } else {
      showToast('error', result[1] || 'Delete failed');
    }
  }, [canDeleteResidents, bulkDeleteTargetIds, getSessionParams, loadResidents, showToast]);

  // Single-row move out — opens the styled confirmation dialog. This used to be a
  // native window.confirm(), which was the only unstyled confirmation left in this
  // flow and couldn't be guided (a browser prompt is outside the DOM, so it can be
  // neither pointed at nor observed).
  const handleMoveOut = useCallback((resident) => {
    const blocked = getAlarmBlockedReason(resident);
    if (blocked) {
      showToast('error', blocked);
      return;
    }
    setMoveOutRow(resident);
  }, [getAlarmBlockedReason, showToast]);

  const handleConfirmMoveOut = useCallback(async () => {
    if (!moveOutRow) return;
    /* An alarm can start while the typed confirmation is being filled in. */
    const blocked = getAlarmBlockedReason(moveOutRow);
    if (blocked) {
      setMoveOutRow(null);
      showToast('error', blocked);
      return;
    }
    const name = `${moveOutRow.resident_first_name || ''} ${moveOutRow.resident_last_name || ''}`.trim();
    setMoveOutSaving(true);
    const session = getSessionParams();
    const result = await moveOutResidents([moveOutRow.resident_id], session);
    setMoveOutSaving(false);
    if (result[0]) {
      setMoveOutRow(null);
      loadResidents();
      showToast('success', `${name} moved out`);
    } else {
      // Leave the dialog open on failure so the user can retry or cancel.
      showToast('error', result[1] || 'Move out failed');
    }
  }, [moveOutRow, getSessionParams, loadResidents, showToast, getAlarmBlockedReason]);

  // Bulk group edit — the drawer resolves add/remove/replace into a full group
  // list per resident and only hands back residents that actually change.
  const handleBulkEditGroups = useCallback(async (changes) => {
    if (!changes || changes.length === 0) {
      setBulkGroupDrawerOpen(false);
      return;
    }
    setBulkGroupSaving(true);
    const session = getSessionParams();
    const result = await bulkEditResidentGroups(changes, session);
    setBulkGroupSaving(false);

    if (result[0]) {
      setBulkGroupDrawerOpen(false);
      setSelectedIds([]);
      loadResidents();
      showToast('success', `Groups updated for ${changes.length} resident${changes.length !== 1 ? 's' : ''}`);
    } else {
      showToast('error', result[1] || 'Failed to update groups');
    }
  }, [getSessionParams, loadResidents, showToast]);

  // Relocate — opened from the row kebab or the Area column shortcut
  const handleOpenRelocate = useCallback((resident) => {
    const blocked = getAlarmBlockedReason(resident);
    if (blocked) {
      showToast('error', blocked);
      return;
    }
    setRelocateResidentRow(resident);
  }, [getAlarmBlockedReason, showToast]);

  const handleRelocate = useCallback(async (newAreaId) => {
    if (!relocateResidentRow) return;
    const blocked = getAlarmBlockedReason(relocateResidentRow);
    if (blocked) {
      setRelocateResidentRow(null);
      showToast('error', blocked);
      return;
    }
    setRelocateSaving(true);
    const session = getSessionParams();
    const result = await relocateResident(
      relocateResidentRow.resident_id,
      newAreaId,
      relocateResidentRow.area_id,
      session,
    );
    setRelocateSaving(false);

    if (result[0]) {
      const name = `${relocateResidentRow.resident_first_name || ''} ${relocateResidentRow.resident_last_name || ''}`.trim();
      setRelocateResidentRow(null);
      loadResidents();
      showToast('success', `${name || 'Resident'} relocated`);
    } else {
      showToast('error', result[1] || 'Relocate failed');
    }
  }, [relocateResidentRow, getSessionParams, loadResidents, showToast, getAlarmBlockedReason]);

  // ==========================================================================
  // GROUPS TAB — Enrich groups with members derived from residents data
  // ==========================================================================
  // The API returns groups with a flat `resident_name` string, not a `members` array.
  // We cross-reference the residents list (each resident has `resident_groups` array)
  // to build the `members` array that GroupResidentsCell and GroupDrawer expect.
  const enrichedGroups = useMemo(() => {
    if (!residents.length) return groups;
    return groups.map((group) => ({
      ...group,
      members: residents
        .filter((r) =>
          r.is_active &&
          (r.resident_groups || []).some((g) => g.resident_group_id === group.resident_group_id)
        )
        .map((r) => ({
          resident_id: r.resident_id,
          resident_name: `${r.resident_first_name} ${r.resident_last_name}`,
          area_name: r.area_name,
          area_type_name: r.area_type_name,
        })),
    }));
  }, [groups, residents]);

  // ==========================================================================
  // GROUPS TAB — Filter / Search / Sort pipeline
  // ==========================================================================
  const filteredGroups = useMemo(() => {
    let data = [...enrichedGroups];

    // 1. Search
    if (groupSearch.trim()) {
      const s = groupSearch.toLowerCase();
      data = data.filter(
        (g) =>
          g.resident_group_name?.toLowerCase().includes(s) ||
          g.resident_description?.toLowerCase().includes(s) ||
          g.resident_group_category_name?.toLowerCase().includes(s) ||
          (g.members || []).some((m) => m.resident_name?.toLowerCase().includes(s))
      );
    }

    // 2. Column filters
    Object.entries(groupFilters).forEach(([columnKey, values]) => {
      if (!values || values.length === 0) return;
      data = data.filter((row) => {
        const val = row[columnKey];
        const strVal = val != null && val !== '' ? String(val) : '(empty)';
        return values.includes(strVal);
      });
    });

    // 3. Sort
    data.sort((a, b) => {
      let aVal, bVal;
      if (groupSortColumn === 'resident_count') {
        aVal = (a.members || []).length;
        bVal = (b.members || []).length;
        const result = aVal - bVal;
        return groupSortOrder === 'asc' ? result : -result;
      }
      aVal = a[groupSortColumn];
      bVal = b[groupSortColumn];
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      const result = String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return groupSortOrder === 'asc' ? result : -result;
    });

    return data;
  }, [enrichedGroups, groupSearch, groupFilters, groupSortColumn, groupSortOrder]);

  // Groups pagination
  const totalFilteredGroups = filteredGroups.length;
  const totalGroupPages = Math.max(1, Math.ceil(totalFilteredGroups / groupPerPage));
  const safeGroupPage = Math.min(groupPage, totalGroupPages);

  const paginatedGroups = useMemo(() => {
    const start = (safeGroupPage - 1) * groupPerPage;
    return filteredGroups.slice(start, start + groupPerPage);
  }, [filteredGroups, safeGroupPage, groupPerPage]);

  const groupShowingStart = totalFilteredGroups === 0 ? 0 : (safeGroupPage - 1) * groupPerPage + 1;
  const groupShowingEnd = Math.min(safeGroupPage * groupPerPage, totalFilteredGroups);

  // Reset group page when filters change
  useEffect(() => { setGroupPage(1); }, [groupSearch, groupFilters, groupPerPage]);

  // Groups page number buttons
  const groupPageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, safeGroupPage - Math.floor(maxButtons / 2));
    let end = Math.min(totalGroupPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [safeGroupPage, totalGroupPages]);

  // Groups handlers
  const handleGroupSort = useCallback(
    (column) => {
      if (groupSortColumn === column) {
        setGroupSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      } else {
        setGroupSortColumn(column);
        setGroupSortOrder('asc');
      }
    },
    [groupSortColumn]
  );

  const handleGroupFilterChange = useCallback((columnKey, values) => {
    setGroupFilters((prev) => ({ ...prev, [columnKey]: values }));
  }, []);

  const handleGroupRemoveFilter = useCallback((columnKey, value) => {
    setGroupFilters((prev) => ({
      ...prev,
      [columnKey]: (prev[columnKey] || []).filter((v) => v !== value),
    }));
  }, []);

  const handleGroupClearAllFilters = useCallback(() => {
    setGroupFilters({});
    setGroupSearch('');
  }, []);

  const handleGroupSelectRow = useCallback((id) => {
    setGroupSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  }, []);

  const handleGroupSelectAll = useCallback(() => {
    const allIds = paginatedGroups.map((g) => g.resident_group_id);
    const allSelected = allIds.every((id) => groupSelectedIds.includes(id));
    if (allSelected) {
      setGroupSelectedIds((prev) => prev.filter((id) => !allIds.includes(id)));
    } else {
      setGroupSelectedIds((prev) => [...new Set([...prev, ...allIds])]);
    }
  }, [paginatedGroups, groupSelectedIds]);

  const handleGroupColumnToggle = useCallback((columnKey) => {
    setGroupVisibleColumns((prev) =>
      prev.includes(columnKey) ? prev.filter((k) => k !== columnKey) : [...prev, columnKey]
    );
  }, []);

  // Group drawer handlers
  const openAddGroup = useCallback(() => {
    setEditingGroup(null);
    setGroupDrawerOpen(true);
  }, []);

  const openEditGroup = useCallback((group) => {
    setEditingGroup(group);
    setGroupDrawerOpen(true);
  }, []);

  const handleSaveGroup = useCallback(
    async (formData) => {
      const session = getSessionParams();
      let result;

      // Build resident_ids array from the members list for the API payload
      const residentIds = (formData.members || []).map((m) => m.resident_id);
      const apiData = { ...formData, resident_ids: residentIds };

      if (formData.resident_group_id) {
        // Edit mode — find original from enriched data for OldPayload
        const original = enrichedGroups.find((g) => g.resident_group_id === formData.resident_group_id);
        const oldResidentIds = (original?.members || []).map((m) => m.resident_id);
        const oldApiData = { ...original, resident_ids: oldResidentIds };
        result = await editGroup(apiData, oldApiData, session);
      } else {
        // Add mode
        result = await addGroup(apiData, session);
      }

      if (result[0]) {
        setGroupDrawerOpen(false);
        loadGroups();
        loadResidents();
        showToast('success', formData.resident_group_id
          ? `${formData.resident_group_name} updated`
          : `${formData.resident_group_name} created`
        );
      } else {
        showToast('error', result[1] || 'Failed to save group');
      }
    },
    [enrichedGroups, getSessionParams, loadGroups, loadResidents, showToast]
  );

  /* Entry point for both the table kebab and the drawer's Delete button — it
     only opens the confirmation. The drawer stays open behind it so cancelling
     doesn't discard unsaved edits, which is why the dialog is `elevated`. */
  const handleDeleteGroup = useCallback((group) => {
    if (!group) return;
    setDeleteGroupRow(group);
  }, []);

  const handleConfirmDeleteGroup = useCallback(async () => {
    const group = deleteGroupRow;
    if (!group) return;
    setDeleteGroupSaving(true);
    const session = getSessionParams();
    const groupName = group.resident_group_name;
    const result = await deleteGroups([group.resident_group_id], session);
    setDeleteGroupSaving(false);
    if (result[0]) {
      setDeleteGroupRow(null);
      setGroupSelectedIds((prev) => prev.filter((id) => id !== group.resident_group_id));
      setGroupDrawerOpen(false);
      loadGroups();
      /* Group membership is shown on the residents table too, so refresh both. */
      loadResidents();
      showToast('success', `${groupName} deleted`);
    } else {
      showToast('error', result[1] || 'Delete failed');
    }
  }, [deleteGroupRow, getSessionParams, loadGroups, loadResidents, showToast]);

  // Group bulk actions
  const handleGroupBulkExport = useCallback(() => {
    const selected = enrichedGroups.filter((g) => groupSelectedIds.includes(g.resident_group_id));
    if (selected.length) exportGroupsCsv(selected, 'resident-groups-selected.csv', myRoleWeight);
  }, [groupSelectedIds, enrichedGroups, myRoleWeight]);

  // Page-level export: exports all currently-filtered groups
  const handleGroupExportAll = useCallback(() => {
    if (filteredGroups.length) exportGroupsCsv(filteredGroups, 'resident-groups.csv', myRoleWeight);
  }, [filteredGroups, myRoleWeight]);

  const handleGroupBulkDelete = useCallback(() => {
    if (groupSelectedIds.length === 0) return;
    setGroupBulkDeleteOpen(true);
  }, [groupSelectedIds.length]);

  const handleConfirmGroupBulkDelete = useCallback(async () => {
    const count = groupSelectedIds.length;
    if (count === 0) return;
    setGroupBulkDeleteSaving(true);
    const session = getSessionParams();
    const result = await deleteGroups(groupSelectedIds, session);
    setGroupBulkDeleteSaving(false);
    if (result[0]) {
      setGroupBulkDeleteOpen(false);
      setGroupSelectedIds([]);
      loadGroups();
      loadResidents();
      showToast('success', `${count} group${count > 1 ? 's' : ''} deleted`);
    } else {
      showToast('error', result[1] || 'Bulk delete failed');
    }
  }, [groupSelectedIds, getSessionParams, loadGroups, loadResidents, showToast]);

  /* Member counts for the delete confirmations. Read from enrichedGroups rather
     than the captured row so the number reflects the latest residents load, and
     count residents distinctly — one resident can belong to several of the
     selected groups. `members` is absent until residents finish loading, in which
     case the count reads 0 and the dialog simply omits the sentence. */
  const deleteGroupMemberCount = useMemo(() => {
    if (!deleteGroupRow) return 0;
    const fresh = enrichedGroups.find(
      (g) => g.resident_group_id === deleteGroupRow.resident_group_id
    );
    return ((fresh || deleteGroupRow).members || []).length;
  }, [deleteGroupRow, enrichedGroups]);

  const groupBulkMemberCount = useMemo(() => {
    if (!groupBulkDeleteOpen) return 0;
    const ids = new Set();
    enrichedGroups.forEach((g) => {
      if (!groupSelectedIds.includes(g.resident_group_id)) return;
      (g.members || []).forEach((m) => ids.add(m.resident_id));
    });
    return ids.size;
  }, [groupBulkDeleteOpen, groupSelectedIds, enrichedGroups]);

  // Group column label map for filter bar
  const groupColumnLabels = useMemo(
    () => Object.fromEntries(GROUP_COLUMN_DEFS.map((c) => [c.key, c.label])),
    []
  );

  // Column label map for filter bar
  const columnLabels = useMemo(
    () => Object.fromEntries(COLUMN_DEFS.map((c) => [c.key, c.label])),
    []
  );

  // The currently-selected campus name — derive from campusList (which correctly maps session.campusData names)
  const campusName = useMemo(() => {
    const match = campusList.find((c) => c.campus_id === campusId);
    return match?.campus_name || '';
  }, [campusId, campusList]);

  // Keep deviceResident in sync when residents array refreshes (e.g. after assign/remove)
  useEffect(() => {
    if (deviceResident && deviceDrawerOpen) {
      const updated = residents.find((r) => r.resident_id === deviceResident.resident_id);
      if (updated && updated !== deviceResident) {
        setDeviceResident(updated);
      }
    }
  }, [residents, deviceResident, deviceDrawerOpen]);

  // Device drawer data
  const deviceDrawerAssigned = deviceResident?.devices || [];

  // Page number buttons
  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxButtons = 5;
    let start = Math.max(1, safePage - Math.floor(maxButtons / 2));
    let end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }, [safePage, totalPages]);

  // Active resident count for tab badge
  const activeCount = residents.filter((r) => r.is_active).length;

  return (
    <>
    <NavigationV2 />
    <div className="nav2-main">
    <div className="rp-page">
      {/* Page-level toast notification */}
      {toast ? (
        <div
          className={`rp-toast ${toast.type === 'success' ? 'rp-toast--success' : 'rp-toast--error'}`}
          role="status"
          aria-live="polite"
        >
          {toast.type === 'success' ? (
            <svg className="rp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="rp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          )}
          <span>{toast.message}</span>
          <button
            type="button"
            onClick={() => { clearTimeout(toastTimerRef.current); setToast(null); }}
            className="rp-toast__close"
            aria-label="Dismiss"
          >
            <svg className="rp-icon-xs" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      ) : <></>}

      {/* ============================================================
          PAGE HEADER AREA
          ============================================================ */}
      <div className="rp-page-header">
        <div className="rp-page-header__inner">
          {/* Breadcrumb + Campus Selector */}
          <div className="rp-breadcrumb-row">
            <div className="rp-breadcrumb">
              <span className="rp-breadcrumb__text">Community</span>
              <svg className="rp-icon-md rp-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
              <span className="rp-breadcrumb__current">Residents</span>
            </div>

            {/* Campus selector — prominent position */}
            <div className="rp-campus-row">
              <label htmlFor="campus-selector" className="rp-campus-label">
                Campus:
              </label>
              {campusList.length > 1 ? (
                <select
                  id="campus-selector"
                  value={campusId}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    setCampusId(val);
                    updateSessionCampus(val);
                  }}
                  className="rp-campus-select"
                >
                  {campusList.map((c) => (
                    <option key={c.campus_id || c.CampusId} value={c.campus_id || c.CampusId}>
                      {c.campus_name || c.CampusName}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="rp-campus-name">{campusName}</span>
              )}
            </div>
          </div>

          {/* Page title */}
          <h1 className="rp-page-title">Residents</h1>
        </div>
      </div>

      {/* ============================================================
          TAB BAR
          ============================================================ */}
      <div className="rp-tab-bar">
        <div className="rp-tab-bar__inner">
          <nav className="rp-tab-nav" aria-label="Tabs">
            <Link to="/community/residents" className={`rp-tab ${activeTab === 'residents' ? 'is-active' : ''}`}>
              Residents ({activeCount})
              {activeTab === 'residents' && (
                <span className="rp-tab__indicator" />
              )}
            </Link>
            <Link to="/community/residents/groups" className={`rp-tab ${activeTab === 'groups' ? 'is-active' : ''}`}>
              Resident Groups ({enrichedGroups.length})
              {activeTab === 'groups' && (
                <span className="rp-tab__indicator" />
              )}
            </Link>
          </nav>
        </div>
      </div>

      {/* ============================================================
          MAIN CONTENT
          ============================================================ */}
      <div className="rp-content">
        {activeTab === 'residents' ? (
          <>
            {/* ---------- Controls Row ---------- */}
            <div className="rp-controls">
              {/* Left: Search + Active toggle */}
              <div className="rp-controls__left">
                {/* Inline search */}
                <div className="rp-search">
                  <svg className="rp-icon-md rp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search residents..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="rp-search__input"
                    aria-label="Search residents"
                  />
                </div>

                {/* Active/All toggle */}
                <div className="rp-toggle-group">
                  <button
                    type="button"
                    onClick={() => setShowInactive(false)}
                    className={`rp-toggle-btn ${!showInactive ? 'is-active' : ''}`}
                  >
                    Active Only ({activeCount})
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowInactive(true)}
                    className={`rp-toggle-btn ${showInactive ? 'is-active' : ''}`}
                  >
                    All ({residents.length})
                  </button>
                </div>
              </div>

              {/* Right: Columns + Export + Add */}
              <div className="rp-controls__right">
                <ColumnVisibilityToggle
                  columns={availableColumns}
                  visibleColumns={visibleColumns}
                  onToggle={handleColumnToggle}
                />

                <button
                  type="button"
                  className="rp-btn rp-btn--secondary"
                  onClick={handleExportAll}
                  aria-label="Export residents"
                >
                  <svg className="rp-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Export
                </button>

                {/* Hidden, not disabled, when the campus lock is on: creating a
                    resident there is the EHR's job, so a greyed-out button would
                    only invite a support call. Matches how the app hides other
                    rights-gated actions (e.g. bulk delete). */}
                {canAddResidents ? (
                  <button
                    type="button"
                    onClick={openAddResident}
                    className="rp-btn rp-btn--primary"
                    aria-label="Add new resident"
                    /* Help-guide anchor (docs/INTERACTIVE_HELP_PLAN.md §5.2) */
                    data-help-id="residents.new-resident"
                  >
                    <svg className="rp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    New Resident
                  </button>
                ) : null}
              </div>
            </div>

            {/* ---------- Active Filter Bar ---------- */}
            <ActiveFilterBar
              filters={filters}
              columnLabels={columnLabels}
              onRemoveFilter={handleRemoveFilter}
              onClearAll={handleClearAllFilters}
            />

            {/* ---------- Bulk Action Bar ---------- */}
            <BulkActionBar
              selectedCount={selectedIds.length}
              onExport={handleBulkExport}
              /* Groups stay available on EHR campuses — the explicit carve-out.
                 Bulk move-out and delete are resident-record writes, so they
                 follow the same gate as the row actions. */
              onEditGroups={() => setBulkGroupDrawerOpen(true)}
              /* Offered whenever at least one selected resident is writable; the
                 confirm dialog names how many are skipped. */
              onMoveOut={bulkMoveOutTargetIds.length > 0 ? handleBulkMoveOut : undefined}
              onDelete={handleBulkDelete}
              canDelete={canDeleteResidents && bulkDeleteTargetIds.length > 0}
              onClearSelection={() => setSelectedIds([])}
            />

            {/* ---------- Table or Skeleton ---------- */}
            {loading ? (
              <TableSkeleton />
            ) : (
              <ResidentTable
                data={paginatedData}
                allData={residents}
                visibleColumns={effectiveVisibleColumns}
                sortColumn={sortColumn}
                sortOrder={sortOrder}
                onSort={handleSort}
                filters={filters}
                onFilterChange={handleFilterChange}
                selectedIds={selectedIds}
                onSelectRow={handleSelectRow}
                onSelectAll={handleSelectAll}
                /* Withholding the handler is how a row action hides — each is
                   guarded on `typeof onX === 'function'` in ResidentTable, the
                   pattern Relocate already used. Manage Devices stays: devices
                   are not EHR-owned. */
                /* Handlers are now passed unconditionally and `isRowLocked` decides
                   per row — it also returns true when the user has no edit role, so the
                   old page-level withholding is fully covered. */
                onEditResident={openEditResident}
                onManageDevices={openDeviceDrawer}
                onMoveOut={handleMoveOut}
                onRelocate={handleOpenRelocate}
                isRowLocked={isRowLocked}
                getAlarmBlockedReason={getAlarmBlockedReason}
                getEditLockedReason={getEditLockedReason}
                alertsByResidentId={residentAlertMap}
                tzOffset={tzOffset}
                onAddResident={canAddResidents ? openAddResident : undefined}
                onClearFilters={handleClearAllFilters}
              />
            )}

            {/* ---------- Pagination ---------- */}
            {!loading && totalFiltered > 0 && (
              <div className="rp-pagination">
                <p className="rp-pagination__info">
                  Showing <span className="rp-pagination__emphasis">{showingStart}</span>–<span className="rp-pagination__emphasis">{showingEnd}</span> of{' '}
                  <span className="rp-pagination__emphasis">{totalFiltered}</span> residents
                </p>

                <div className="rp-pagination__controls">
                  {/* Rows per page */}
                  <div className="rp-per-page">
                    <label htmlFor="per-page" className="rp-per-page__label">Rows per page:</label>
                    <select
                      id="per-page"
                      value={perPage}
                      onChange={(e) => setPerPage(parseInt(e.target.value, 10))}
                      className="rp-per-page__select"
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                      <option value={300}>300</option>
                    </select>
                  </div>

                  {/* Page buttons */}
                  <nav className="rp-page-nav" aria-label="Pagination">
                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={safePage <= 1}
                      className="rp-page-btn"
                      aria-label="Previous page"
                    >
                      <svg className="rp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                      </svg>
                    </button>

                    {pageNumbers.map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => setPage(num)}
                        className={`rp-page-num ${num === safePage ? 'is-active' : ''}`}
                        aria-label={`Page ${num}`}
                        aria-current={num === safePage ? 'page' : undefined}
                      >
                        {num}
                      </button>
                    ))}

                    <button
                      type="button"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={safePage >= totalPages}
                      className="rp-page-btn"
                      aria-label="Next page"
                    >
                      <svg className="rp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            )}
          </>
        ) : (
          /* ============================================================
              GROUPS TAB
              ============================================================ */
          <>
            {/* ---------- Groups Controls Row ---------- */}
            <div className="rp-controls">
              <div className="rp-controls__left">
                <div className="rp-search">
                  <svg className="rp-icon-md rp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                  </svg>
                  <input
                    type="search"
                    placeholder="Search groups..."
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                    className="rp-search__input"
                    aria-label="Search groups"
                  />
                </div>
              </div>

              <div className="rp-controls__right">
                <ColumnVisibilityToggle
                  columns={GROUP_COLUMN_DEFS}
                  visibleColumns={groupVisibleColumns}
                  onToggle={handleGroupColumnToggle}
                />

                <button
                  type="button"
                  className="rp-btn rp-btn--secondary"
                  onClick={handleGroupExportAll}
                  aria-label="Export groups"
                >
                  <svg className="rp-icon-md" style={{ color: 'var(--color-text-secondary)' }} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Export
                </button>

                <button
                  type="button"
                  onClick={openAddGroup}
                  className="rp-btn rp-btn--primary"
                  aria-label="Add new group"
                >
                  <svg className="rp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  New Group
                </button>
              </div>
            </div>

            {/* ---------- Groups Active Filter Bar ---------- */}
            <ActiveFilterBar
              filters={groupFilters}
              columnLabels={groupColumnLabels}
              onRemoveFilter={handleGroupRemoveFilter}
              onClearAll={handleGroupClearAllFilters}
            />

            {/* ---------- Groups Bulk Action Bar ---------- */}
            {groupSelectedIds.length > 0 && (
              <div className="rp-bulk-bar">
                <div className="rp-bulk-bar__left">
                  <span className="rp-bulk-bar__count">
                    {groupSelectedIds.length} group{groupSelectedIds.length !== 1 ? 's' : ''} selected
                  </span>
                  <button
                    type="button"
                    onClick={() => setGroupSelectedIds([])}
                    className="rp-bulk-bar__deselect"
                  >
                    Deselect All
                  </button>
                </div>
                <div className="rp-bulk-bar__right">
                  <button
                    type="button"
                    onClick={handleGroupBulkExport}
                    className="rp-bulk-btn"
                    aria-label="Export selected groups"
                  >
                    <svg className="rp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                    </svg>
                    Export
                  </button>
                  <button
                    type="button"
                    onClick={handleGroupBulkDelete}
                    className="rp-bulk-btn rp-bulk-btn--danger"
                    aria-label="Delete selected groups"
                  >
                    <svg className="rp-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            )}

            {/* ---------- Groups Table or Skeleton ---------- */}
            {groupsLoading ? (
              <TableSkeleton />
            ) : (
              <GroupsTable
                data={paginatedGroups}
                allData={enrichedGroups}
                visibleColumns={groupVisibleColumns}
                sortColumn={groupSortColumn}
                sortOrder={groupSortOrder}
                onSort={handleGroupSort}
                filters={groupFilters}
                onFilterChange={handleGroupFilterChange}
                selectedIds={groupSelectedIds}
                onSelectRow={handleGroupSelectRow}
                onSelectAll={handleGroupSelectAll}
                onEditGroup={openEditGroup}
                onDeleteGroup={handleDeleteGroup}
                onAddGroup={openAddGroup}
                onClearFilters={handleGroupClearAllFilters}
              />
            )}

            {/* ---------- Groups Pagination ---------- */}
            {!groupsLoading && totalFilteredGroups > 0 && (
              <div className="rp-pagination">
                <p className="rp-pagination__info">
                  Showing <span className="rp-pagination__emphasis">{groupShowingStart}</span>–<span className="rp-pagination__emphasis">{groupShowingEnd}</span> of{' '}
                  <span className="rp-pagination__emphasis">{totalFilteredGroups}</span> groups
                </p>

                <div className="rp-pagination__controls">
                  <div className="rp-per-page">
                    <label htmlFor="group-per-page" className="rp-per-page__label">Rows per page:</label>
                    <select
                      id="group-per-page"
                      value={groupPerPage}
                      onChange={(e) => setGroupPerPage(parseInt(e.target.value, 10))}
                      className="rp-per-page__select"
                    >
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                      <option value={300}>300</option>
                    </select>
                  </div>

                  <nav className="rp-page-nav" aria-label="Groups pagination">
                    <button
                      type="button"
                      onClick={() => setGroupPage((p) => Math.max(1, p - 1))}
                      disabled={safeGroupPage <= 1}
                      className="rp-page-btn"
                      aria-label="Previous page"
                    >
                      <svg className="rp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                      </svg>
                    </button>

                    {groupPageNumbers.map((num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() => setGroupPage(num)}
                        className={`rp-page-num ${num === safeGroupPage ? 'is-active' : ''}`}
                        aria-label={`Page ${num}`}
                        aria-current={num === safeGroupPage ? 'page' : undefined}
                      >
                        {num}
                      </button>
                    ))}

                    <button
                      type="button"
                      onClick={() => setGroupPage((p) => Math.min(totalGroupPages, p + 1))}
                      disabled={safeGroupPage >= totalGroupPages}
                      className="rp-page-btn"
                      aria-label="Next page"
                    >
                      <svg className="rp-icon-md" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ============================================================
          DRAWERS
          ============================================================ */}
      <ResidentDrawer
        isOpen={residentDrawerOpen}
        onClose={() => setResidentDrawerOpen(false)}
        resident={editingResident}
        onSave={handleSaveResident}
        areas={areas}
        groups={groupList}
        offsiteActive={getOffsiteMonitoringVendors().some((v) => v.vendor_active)}
        residents={residents}
        ehrActive={ehrActive}
      />

      {/* Bulk group edit drawer */}
      <BulkGroupDrawer
        isOpen={bulkGroupDrawerOpen}
        onClose={() => setBulkGroupDrawerOpen(false)}
        onSave={handleBulkEditGroups}
        groups={groupList}
        residents={residents.filter((r) => selectedIds.includes(r.resident_id))}
        saving={bulkGroupSaving}
      />

      {/* Relocate Dialog — shared with the resident profile page */}
      {relocateResidentRow ? (
        <RelocateDialog
          currentAreaId={relocateResidentRow.area_id}
          locationData={areas}
          onClose={() => setRelocateResidentRow(null)}
          onRelocate={handleRelocate}
          saving={relocateSaving}
        />
      ) : null}

      {/* Move Out Dialog — shared with the resident profile page. Replaced a
          native window.confirm() so both entry points match and the confirm can
          be guided. */}
      {moveOutRow ? (
        <MoveOutDialog
          residentName={`${moveOutRow.resident_first_name || ''} ${moveOutRow.resident_last_name || ''}`.trim()}
          onClose={() => setMoveOutRow(null)}
          onConfirm={handleConfirmMoveOut}
          saving={moveOutSaving}
        />
      ) : null}

      {/* Bulk move out — same wording as the single dialog, pluralised. No
          help-guide anchors: the guided flow follows the single-resident path,
          and a duplicate anchor id would give the spotlight two targets. */}
      {bulkMoveOutOpen ? (
        <ConfirmTypedDialog
          title={`Move Out ${bulkMoveOutTargetIds.length} Resident${bulkMoveOutTargetIds.length > 1 ? 's' : ''}`}
          confirmLabel="Move Out"
          savingLabel="Moving Out…"
          onClose={() => setBulkMoveOutOpen(false)}
          onConfirm={handleConfirmBulkMoveOut}
          saving={bulkMoveOutSaving}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            Are you sure you want to move out{' '}
            <strong>{bulkMoveOutTargetIds.length} resident{bulkMoveOutTargetIds.length > 1 ? 's' : ''}</strong>?
          </p>
          {bulkLockedCount > 0 ? (
            <p style={{ margin: 'var(--space-2) 0 0', lineHeight: 1.6 }}>
              <strong>{bulkLockedCount}</strong> of the {selectedIds.length} selected
              {bulkLockedCount > 1 ? ' are' : ' is'} managed by the EHR integration and will
              be skipped. Move {bulkLockedCount > 1 ? 'them' : 'it'} out in the partner
              system instead.
            </p>
          ) : null}
          {bulkAlarmCount > 0 ? (
            <p style={{ margin: 'var(--space-2) 0 0', lineHeight: 1.6 }}>
              <strong>{bulkAlarmCount}</strong> of the {selectedIds.length} selected
              {bulkAlarmCount > 1 ? ' have' : ' has'} an active alarm and will be skipped. Try
              again once {bulkAlarmCount > 1 ? 'those alerts are' : 'that alert is'} resolved.
            </p>
          ) : null}
          <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            This will deactivate {selectedIds.length > 1 ? 'them' : 'the resident'}. Their devices and wellness
            assignments will no longer be active. You can move them back in later.
          </p>
        </ConfirmTypedDialog>
      ) : null}

      {/* Bulk delete — the irreversible one. */}
      {bulkDeleteOpen ? (
        <ConfirmTypedDialog
          title={`Delete ${bulkDeleteTargetIds.length} Resident${bulkDeleteTargetIds.length > 1 ? 's' : ''}`}
          confirmLabel="Delete"
          savingLabel="Deleting…"
          onClose={() => setBulkDeleteOpen(false)}
          onConfirm={handleConfirmBulkDelete}
          saving={bulkDeleteSaving}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            Are you sure you want to delete{' '}
            <strong>{bulkDeleteTargetIds.length} resident{bulkDeleteTargetIds.length > 1 ? 's' : ''}</strong>?
          </p>
          {bulkDeleteAlarmCount > 0 ? (
            <p style={{ margin: 'var(--space-2) 0 0', lineHeight: 1.6 }}>
              <strong>{bulkDeleteAlarmCount}</strong> of the {selectedIds.length} selected
              {bulkDeleteAlarmCount > 1 ? ' have' : ' has'} an active alarm and will be skipped.
            </p>
          ) : null}
          <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            {bulkDeleteTargetIds.length > 1 ? 'These residents' : 'This resident'} will no longer appear anywhere in the
            system. <strong>This cannot be undone.</strong> To keep their history and be able to bring them back,
            move them out instead.
          </p>
        </ConfirmTypedDialog>
      ) : null}

      {/* Delete a single resident group — reachable from the groups table kebab
          and from the group drawer's Delete button. Elevated because the drawer
          path leaves the drawer open underneath. */}
      {deleteGroupRow ? (
        <ConfirmTypedDialog
          title="Delete Group"
          confirmLabel="Delete"
          savingLabel="Deleting…"
          elevated
          onClose={() => setDeleteGroupRow(null)}
          onConfirm={handleConfirmDeleteGroup}
          saving={deleteGroupSaving}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            Are you sure you want to delete <strong>{deleteGroupRow.resident_group_name}</strong>?
          </p>
          <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            {deleteGroupMemberCount > 0 ? (
              <>
                This group will be removed from the{' '}
                <strong>
                  {deleteGroupMemberCount} resident{deleteGroupMemberCount > 1 ? 's' : ''}
                </strong>{' '}
                assigned to it.{' '}
              </>
            ) : null}
            The residents themselves are not deleted. <strong>This cannot be undone.</strong>
          </p>
        </ConfirmTypedDialog>
      ) : null}

      {/* Bulk delete resident groups */}
      {groupBulkDeleteOpen ? (
        <ConfirmTypedDialog
          title={`Delete ${groupSelectedIds.length} Group${groupSelectedIds.length > 1 ? 's' : ''}`}
          confirmLabel="Delete"
          savingLabel="Deleting…"
          onClose={() => setGroupBulkDeleteOpen(false)}
          onConfirm={handleConfirmGroupBulkDelete}
          saving={groupBulkDeleteSaving}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            Are you sure you want to delete{' '}
            <strong>{groupSelectedIds.length} group{groupSelectedIds.length > 1 ? 's' : ''}</strong>?
          </p>
          <p style={{ margin: 'var(--space-2) 0 0', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
            {groupBulkMemberCount > 0 ? (
              <>
                {groupSelectedIds.length > 1 ? 'These groups' : 'This group'} will be removed from{' '}
                <strong>
                  {groupBulkMemberCount} resident{groupBulkMemberCount > 1 ? 's' : ''}
                </strong>
                .{' '}
              </>
            ) : null}
            The residents themselves are not deleted. <strong>This cannot be undone.</strong>
          </p>
        </ConfirmTypedDialog>
      ) : null}

      <DeviceDrawer
        isOpen={deviceDrawerOpen}
        onClose={() => setDeviceDrawerOpen(false)}
        resident={deviceResident}
        assignedDevices={deviceDrawerAssigned}
        availableDevices={availableDevices}
        onAssignDevice={handleAssignDevice}
        onRemoveDevice={handleRemoveDevice}
        alertedDeviceIds={alertedDeviceIds}
        onAddNewDevice={() => setEasyAddOpen(true)}
      />

      {/* Simplified add-and-assign flow for brand-new devices */}
      <EasyAddDeviceDrawer
        isOpen={easyAddOpen}
        onClose={() => setEasyAddOpen(false)}
        mode="resident"
        classPrefix="rp"
        roleWeight={myRoleWeight}
        target={deviceResident}
        existingDevices={availableDevices}
        campusId={campusId}
        sessionId={getSessionId()}
        onAdd={handleEasyAddDevice}
        onAssignExisting={handleEasyAssignExisting}
      />

      <GroupDrawer
        isOpen={groupDrawerOpen}
        onClose={() => setGroupDrawerOpen(false)}
        group={editingGroup}
        onSave={handleSaveGroup}
        onDelete={handleDeleteGroup}
        categories={groupCategories}
        residents={residents}
      />
    </div>
    </div>
    </>
  );
};

export default ResidentsPage;
